import { Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import React from 'react';

type TransitionDirection = 'forward' | 'backward' | 'fade' | 'inline-forward' | 'inline-backward';

const enterForward = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to { opacity: 1; transform: none; }
`;

const enterBackward = keyframes`
  from { opacity: 0; transform: translateY(-24px); }
  to { opacity: 1; transform: none; }
`;

const enterInlineForward = keyframes`
  from { opacity: 0; transform: translateX(8px); }
  to { opacity: 1; transform: none; }
`;

const enterInlineBackward = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: none; }
`;

const enterFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/** Animation name, duration and easing per direction. */
const TRANSITIONS: Record<
  TransitionDirection,
  { name: typeof enterForward; duration: string; easing: string }
> = {
  'inline-forward': {
    name: enterInlineForward,
    duration: '160ms',
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  },
  'inline-backward': {
    name: enterInlineBackward,
    duration: '160ms',
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  },
  forward: { name: enterForward, duration: '440ms', easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  backward: { name: enterBackward, duration: '440ms', easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  fade: { name: enterFade, duration: '240ms', easing: 'ease' },
};

interface StepTransitionProps {
  /** Must change when the active step changes to trigger the transition. */
  stepKey: string;
  direction: TransitionDirection;
  children: React.ReactNode;
}

/**
 * Keyed enter-only transition for wizard step changes, in pure CSS. Motion is
 * vertical (Trade Republic style): forward content rises up from the bottom,
 * backward content descends from the top.
 *
 * - forward:  enters from y +32 (440ms, organic ease-out)
 * - backward: enters from y -24 (440ms, organic ease-out)
 * - inline-forward/backward: subtle horizontal 8px menu drill-in (160ms)
 * - fade:     opacity only (240ms) — used for intro → dna
 *
 * Deliberately NOT framer-motion: JS-driven mount animations freeze at
 * opacity 0 when requestAnimationFrame is throttled (occluded webview,
 * background tab), leaving the whole step invisible. A CSS animation with
 * fill-mode backwards keeps the element's natural state visible — if the
 * animation cannot run, the content simply appears without the entrance.
 * The key change remounts the subtree and replays the animation.
 *
 * prefers-reduced-motion is honoured via media query (animation removed).
 */
const StepTransition: React.FC<StepTransitionProps> = ({ stepKey, direction, children }) => {
  const { name, duration, easing } = TRANSITIONS[direction];

  return (
    <Box
      key={stepKey}
      data-step={stepKey}
      w="full"
      css={{
        animation: `${name} ${duration} ${easing} backwards`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {children}
    </Box>
  );
};

export default StepTransition;
